import React, {useState} from "react";
import {useParams} from "react-router-dom";
import {
    useCreateComputeSeeds,
    useCreateHeatRegistration,
    useGetHeat,
    useGetHeatRegistrations,
    useGetNotRegistered,
    useRemoveHeatRegistration
} from "./heatHooks";
import {type HeatRegistration, HeatStatus, HeatType, type Player, type PlayerRegistration} from '@iziping/common';
import {useTranslation} from "react-i18next";
import {useQueryClient} from "@tanstack/react-query";
import { QUERY_CACHE } from '@/model/endpoints';
import Modal from "../modal/Modal";
import HeatInitialization from "./HeatInitialization";
import HeatRegistrationCard from "../shared/HeatRegistrationCard";
import { useNotification } from '@iziping/ui';
import {UserMinus} from "lucide-react";

const HeatManagement: React.FC = () => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const {id} = useParams();
    const {data: registrations, isLoading, isError} = useGetHeatRegistrations(Number(id));
    const {data: heat, isLoading: isHeatLoading, isError: isHeatError} = useGetHeat(Number(id));
    const {
        data: notRegistered,
        isLoading: loadingNotRegistered,
        isError: errorNotRegistered
    } = useGetNotRegistered(id ?? '');
    const createRegistration = useCreateHeatRegistration();
    const removeRegistration = useRemoveHeatRegistration();
    const computeSeeds = useCreateComputeSeeds();
    const {notify} = useNotification();

    const [showModal, setShowModal] = useState(false);

    const toggleModal = () => setShowModal(!showModal);

    const handleGenerateSeeds = async () => {
        try {
            await computeSeeds.mutateAsync({heatId: id});
            await queryClient.invalidateQueries({queryKey: [QUERY_CACHE.HEAT_REGISTRATIONS]})
        } catch (error: any) {
            notify(t(error.error), 'error');
        }

    };

    const handleRegistrationToHeat = async (player: Player) => {
        try {
            await createRegistration.mutateAsync({
                players: [player], heatId: id
            });
            await queryClient.invalidateQueries({queryKey: [QUERY_CACHE.HEAT_REGISTRATIONS]})
            await queryClient.invalidateQueries({queryKey: [QUERY_CACHE.HEAT_NOT_REGISTERED]})
            notify(t('heat.player.registered'), 'success');
        } catch (error: any) {
            notify(t(error.error), 'error');
        }
    };

    const handleRemoveRegistration = async (registrationId: number) => {
        try {
            await removeRegistration.mutateAsync({id: id ?? '', registrationId: registrationId});
            await queryClient.invalidateQueries({queryKey: [QUERY_CACHE.HEAT_REGISTRATIONS]})
            await queryClient.invalidateQueries({queryKey: [QUERY_CACHE.HEAT_NOT_REGISTERED]})
            notify(t('heat.player.unregistered'), 'success');
        } catch (error: any) {
            notify(t(error.error), 'error');
        }
    };

    if (isLoading) return <div>Loading...</div>;
    if (isError) return <div>Error fetching data</div>;


    return (
        <div className={'page'}>
            {heat && (
                <div>
                    <h2>{heat.name} ({t(`HEATTYPE.${heat.type}`)})</h2>
                    {heat.status === HeatStatus.PREPARATION &&
                        <button className={'button'} onClick={() => handleGenerateSeeds()}>{t('HEAT_GENERATE_SEEDS')}</button>
                    }
                    <button className={'button'} onClick={toggleModal}>{t('HEAT_MANAGE_FIRST_ROUND')}</button>
                </div>
            )}
            {(heat?.type === HeatType.DOUBLE) ? (
                <div>
                    <button className={'button'}>Inscrire paire</button>
                </div>
            ) : null}

            {showModal && (
                <Modal onClose={toggleModal} fullScreen={true}>
                    <HeatInitialization heat={heat!}/>
                </Modal>
            )}
            <div className={'container-column'}>
                <div className={'left'}>
                    <h3>{t('HEAT_LIST_NOT_REGISTERED')}</h3>
                    {notRegistered && notRegistered.length > 0 ?
                        <table className={'table-stripped'}>
                            <thead>
                                <tr>
                                    <th>{t('PLAYER_LASTNAME')}</th>
                                    <th>{t('PLAYER_FIRSTNAME')}</th>
                                    <th>{t('PLAYER_POINTS')}</th>
                                    <th>{t('PLAYER_CLUB')}</th>
                                </tr>
                            </thead>
                            <tbody>
                            {notRegistered?.map((registration: PlayerRegistration) => (
                                registration && registration.player &&
                                <tr key={registration.player.id}
                                    onClick={() => handleRegistrationToHeat(registration.player)}>
                                    <td>{registration.player.lastname}</td>
                                    <td>{registration.player.firstname}</td>
                                    <td>{registration.player.points}</td>
                                    <td>{registration.player.club?.name || '-'}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                        : <div>{t('HEAT_LIST_NOT_REGISTERED_EMPTY')}</div>}
                </div>
                <div className={'right'}>
                    <h3>{registrations?.length} {heat?.type === HeatType.DOUBLE ? t('HEAT_LIST_TEAMS_REGISTERED') : t('HEAT_LIST_PLAYERS_REGISTERED')}</h3>
                    <table className={'table-stripped'}>
                        <thead>
                            <tr>
                                <th>Détail</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                        {registrations?.map((registration: HeatRegistration) => (
                            registration && registration.players &&
                            <tr key={registration.players[0].id}>
                                <td><HeatRegistrationCard registration={registration}/></td>
                                <td><UserMinus fill="currentColor" onClick={() => handleRemoveRegistration(registration.id)}/></td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default HeatManagement;